import type { Presupuesto, PresupuestoItem } from '@/types/database'

/**
 * Safely parse items from presupuesto JSON field.
 * Handles string, parsed JSON, and null/undefined cases.
 */
export function parsePresupuestoItems(presupuesto: Presupuesto): PresupuestoItem[] {
  if (!presupuesto.items) {
    return []
  }
  if (typeof presupuesto.items === 'string') {
    try {
      return JSON.parse(presupuesto.items) as PresupuestoItem[]
    } catch {
      return []
    }
  }
  return presupuesto.items as unknown as PresupuestoItem[]
}

/**
 * Format presupuesto number as #0001
 */
export function formatNumeroPresupuesto(numero: number | null | undefined): string {
  return `#${(numero ?? 0).toString().padStart(4, '0')}`
}

/**
 * Check if a presupuesto is expired (enviado + past validity date)
 */
export function isPresupuestoVencido(fecha: string | null, validezDias: number | null, estado: string | null): boolean {
  if (estado !== 'enviado') return false
  if (!fecha) return false
  const fechaValidez = new Date(fecha)
  fechaValidez.setDate(fechaValidez.getDate() + (validezDias ?? 0))
  return new Date() > fechaValidez
}

/**
 * Check if a presupuesto can be edited (only borrador)
 */
export function isPresupuestoEditable(estado: string | null): boolean {
  return estado === 'borrador'
}

/**
 * AFIP valid IVA rates
 */
export const ALICUOTAS_IVA = [
  { value: '21', label: '21%' },
  { value: '10.5', label: '10,5%' },
  { value: '27', label: '27%' },
  { value: '0', label: '0%' },
  { value: 'exento', label: 'Exento' },
  { value: 'no_gravado', label: 'No Gravado' },
] as const

/**
 * Payment conditions options
 */
export const CONDICIONES_PAGO = [
  { value: 'contado', label: 'Contado' },
  { value: '30_dias', label: '30 días' },
  { value: '60_dias', label: '60 días' },
  { value: '90_dias', label: '90 días' },
  { value: 'personalizado', label: 'Personalizado' },
] as const

/**
 * Get numeric IVA percentage from string value
 */
export function getIvaPorcentaje(value: string): number {
  if (value === 'exento' || value === 'no_gravado') return 0
  return parseFloat(value) || 0
}

export type TipoPersona = 'fisica' | 'juridica' | 'sucesion'

/**
 * Detect tipo_persona from CUIT prefix
 */
export function detectarTipoPersona(cuit: string | null): TipoPersona {
  if (!cuit) return 'fisica'
  const clean = cuit.replace(/\D/g, '')
  if (clean.startsWith('30') || clean.startsWith('33') || clean.startsWith('34')) return 'juridica'
  if (clean.startsWith('20') || clean.startsWith('23') || clean.startsWith('24') || clean.startsWith('27')) return 'fisica'
  return 'fisica'
}

/**
 * Format CUIT as XX-XXXXXXXX-X
 */
export function formatCuit(cuit: string | null): string {
  if (!cuit) return '-'
  const clean = cuit.replace(/\D/g, '')
  if (clean.length === 11) {
    return `${clean.slice(0, 2)}-${clean.slice(2, 10)}-${clean.slice(10)}`
  }
  return cuit
}

/**
 * Check if CUIT belongs to persona juridica (30, 33, 34 prefix)
 */
export function esPersonaJuridica(cuit: string | null): boolean {
  if (!cuit) return false
  const clean = cuit.replace(/\D/g, '')
  return clean.startsWith('30') || clean.startsWith('33') || clean.startsWith('34')
}

// --- Nombre de archivo (ORR-120) ---

/**
 * Sanitize a string into a safe filename segment: strip accents, replace any
 * non-alphanumeric run with a single dash, and trim leading/trailing dashes.
 * "García López" -> "Garcia-Lopez" · "Empresa XYZ S.A." -> "Empresa-XYZ-S-A"
 */
export function sanitizarSegmentoArchivo(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export interface DatosNombreCliente {
  /** persona.tipo: 'fisica' | 'juridica' | 'sucesion_indivisa' (real, si está disponible) */
  tipo?: string | null
  apellido?: string | null
  razonSocial?: string | null
  nombreCompleto?: string | null
  /** Denormalized name stored on the presupuesto (fallback when cliente_id is absent) */
  clienteNombre?: string | null
  /** CUIT to infer física/jurídica when persona.tipo is unavailable */
  cuit?: string | null
}

/**
 * Resolve the base label for a presupuesto filename.
 * - Jurídica → razón social (fallbacks: nombre_completo → cliente_nombre).
 * - Física / sucesión → apellido. Fallback (sin apellido estructurado): derive
 *   from nombre_completo/cliente_nombre using the project's "first token =
 *   nombre, rest = apellido" heuristic (clientesService).
 * Returns '' when nothing usable is available (caller falls back to número).
 */
export function resolverBaseNombre({
  tipo,
  apellido,
  razonSocial,
  nombreCompleto,
  clienteNombre,
  cuit,
}: DatosNombreCliente): string {
  const esJuridica = tipo ? tipo === 'juridica' : detectarTipoPersona(cuit ?? null) === 'juridica'

  if (esJuridica) {
    return (razonSocial || nombreCompleto || clienteNombre || '').trim()
  }

  if (apellido && apellido.trim()) return apellido.trim()

  const fuente = (nombreCompleto || clienteNombre || '').trim()
  if (!fuente) return ''
  const partes = fuente.split(/\s+/)
  return partes.length > 1 ? partes.slice(1).join(' ') : fuente
}

/**
 * Build the download filename for a presupuesto: `Presupuesto-{Apellido}.pdf`
 * (físicas) / `Presupuesto-{RazónSocial}.pdf` (jurídicas). When no usable name
 * is available, fall back to the número (legacy behaviour).
 */
export function nombreArchivoPresupuesto(base: string | null | undefined, fallbackNumero?: number | null): string {
  const limpio = sanitizarSegmentoArchivo(base)
  if (limpio) return `Presupuesto-${limpio}.pdf`
  return `Presupuesto-${(fallbackNumero ?? 0).toString().padStart(4, '0')}.pdf`
}
